import { Chip } from '@/dsm-components';
import { useGetRequirementFlagSettingsQuery } from '@/services/requirementFlagSettings';
import { useGetUtilitiesQuery } from '@/services/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback, useMemo } from 'react';

import {
  ChecklistConditionName,
  ChecklistConditionNameLabels,
  ChecklistConditionOperator,
  ChecklistEvaluationKey,
  ChecklistItemInputType,
  documentTitles,
  FinanceTypeLabels,
  FlagPurposeLabel,
  ProgramType,
  ProgramTypeLabels,
  RequirementFlagSettingsDocument,
} from 'types';
import type { EvaluationParams } from 'types';

import { ChecklistItemEditForm } from './ChecklistItemEditForm';
import {
  ChecklistConditionOperatorLabels,
  evaluationKeyLabels,
  inputTypeOptions,
} from './checklistItemInputOptions';
import { TemplateItemFormValue, getStableId } from './ChecklistTemplateItemsEditor';
import { evaluationTemplateRegistry } from './evaluationTemplateRegistry';

interface EvaluationToleranceSummaryProps {
  evaluationKey: ChecklistEvaluationKey;
  params: EvaluationParams | undefined;
}

const EvaluationToleranceSummary = ({ evaluationKey, params }: EvaluationToleranceSummaryProps) => {
  const descriptor = evaluationTemplateRegistry[evaluationKey];
  if (!descriptor) return null;

  const { Summary } = descriptor;
  return <Summary params={params} />;
};

export interface ChecklistItemCardProps {
  item: TemplateItemFormValue;
  readOnly?: boolean;
  contained?: boolean;
  isEditing?: boolean;
  disableInteractions?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
  onSave?: (item: TemplateItemFormValue) => void;
  onCancelEdit?: () => void;
  programTypes?: string[];
}

const getConditionParamLabel = (
  conditionName: string,
  param: string,
  utilityNameMap: Record<string, string>,
): string => {
  if (conditionName === ChecklistConditionName.financeType) {
    return FinanceTypeLabels[param as keyof typeof FinanceTypeLabels] || param;
  }
  if (conditionName === ChecklistConditionName.programType) {
    return ProgramTypeLabels[param as keyof typeof ProgramTypeLabels] || param;
  }
  if (conditionName === ChecklistConditionName.utility) {
    return utilityNameMap[param] || param;
  }
  return param;
};

const getOperatorDisplayText = (operator: ChecklistConditionOperator | undefined): string =>
  ChecklistConditionOperatorLabels[operator ?? ChecklistConditionOperator.Is];

export const ChecklistItemCard = ({
  item,
  readOnly = false,
  contained = false,
  isEditing = false,
  disableInteractions = false,
  onEdit,
  onRemove,
  onSave,
  onCancelEdit,
  programTypes = [],
}: ChecklistItemCardProps) => {
  const sortable = useSortable({
    id: readOnly ? (item.key ?? 'readonly') : getStableId(item),
    disabled: isEditing || readOnly || disableInteractions,
  });

  const style = readOnly
    ? {}
    : {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.5 : 1,
      };

  const hasUtilityCondition =
    (item.conditions?.some((c) => c.name === ChecklistConditionName.utility) ?? false) ||
    (item.inputs?.some((i) => i.conditions?.some((c) => c.name === ChecklistConditionName.utility)) ?? false);
  const { data: utilitiesData } = useGetUtilitiesQuery({}, { skip: !hasUtilityCondition });
  const utilityNameMap = useMemo(() => {
    if (!utilitiesData) return {};
    return Object.fromEntries(
      utilitiesData.map((u: { lseId: number; name: string }) => [u.lseId.toString(), u.name]),
    );
  }, [utilitiesData]);

  const hasFlagSettingLink = item.inputs?.some((i) => !!i.requirementFlagSettingId) ?? false;
  const { data: flagSettingsData } = useGetRequirementFlagSettingsQuery(
    {
      type: undefined,
      pageSize: 1000,
      programTypes: programTypes.length > 0 ? (programTypes as ProgramType[]) : undefined,
    },
    { skip: !hasFlagSettingLink },
  );
  const flagSettingNameMap = useMemo(() => {
    const settings: RequirementFlagSettingsDocument[] = flagSettingsData?.requirementFlagSettings ?? [];
    return Object.fromEntries(settings.map((s) => [s.id?.toString() ?? '', s.name]));
  }, [flagSettingsData]);

  const handleEdit = useCallback(() => {
    onEdit?.();
  }, [onEdit]);

  const handleRemove = useCallback(() => {
    onRemove?.();
  }, [onRemove]);

  if (isEditing) {
    return (
      <div
        ref={sortable.setNodeRef}
        style={style}
        className="rounded-[6px] border-2 border-input-border-default bg-canvas-subtle p-16"
      >
        <ChecklistItemEditForm item={item} onSave={onSave!} onCancel={onCancelEdit!} programTypes={programTypes} />
      </div>
    );
  }

  const hasConditions = item.conditions && item.conditions.length > 0;
  const hasInputs = item.inputs && item.inputs.length > 0;
  const hasDocumentTypes = item.documentTypes && item.documentTypes.length > 0;

  const actionButtonClassName = disableInteractions
    ? 'cursor-not-allowed border-none bg-transparent p-0 leading-none text-text-tertiary opacity-50'
    : 'cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary';

  return (
    <div
      ref={readOnly ? undefined : sortable.setNodeRef}
      style={style}
      className={`rounded-[6px] p-16 ${contained ? 'bg-canvas-default' : 'bg-canvas-highlight-default'}`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-8">
          {!readOnly && (
            <div
              {...sortable.attributes}
              {...sortable.listeners}
              className="shrink-0 rotate-90 cursor-grab touch-none"
            >
              <PalmettoIcon name="dragReorderDots" size="md" />
            </div>
          )}
          <span className="shrink-0 body-bold-300 text-text-primary">{item.name}</span>

          {!readOnly && (
            <div className="ml-auto flex shrink-0 items-center gap-24">
              <button
                type="button"
                onClick={handleEdit}
                disabled={disableInteractions}
                aria-disabled={disableInteractions}
                aria-label="Edit item"
                className={actionButtonClassName}
              >
                <PalmettoIcon name="penEdit" size="sm" />
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={disableInteractions}
                aria-disabled={disableInteractions}
                aria-label="Remove item"
                className={actionButtonClassName}
              >
                <PalmettoIcon name="trash" size="sm" />
              </button>
            </div>
          )}
        </div>

        {hasConditions && (
          <div className={`flex flex-col gap-2 ${readOnly ? '' : 'pl-[46px]'}`}>
            {item.conditions!.map((condition, idx) => {
              const label = ChecklistConditionNameLabels[condition.name as ChecklistConditionName] || condition.name;
              return (
                <div key={`${condition.name}-${idx}`} className="flex flex-wrap items-center gap-12">
                  <span className={`font-medium body-300 ${idx === 0 ? 'invisible' : 'text-text-tertiary'}`}>
                    and
                  </span>
                  <Chip variant="lumen">{label}</Chip>
                  <span className="body-300 text-text-tertiary">{getOperatorDisplayText(condition.operator)}</span>
                  {condition.params && condition.params.length > 0 && (
                    <>
                      {condition.params.map((param, paramIdx) => (
                        <span key={param} className="contents">
                          {paramIdx > 0 && <span className="body-200 text-text-tertiary">or</span>}
                          <Chip
                            variant="lumen"
                            className={
                              condition.operator === ChecklistConditionOperator.IsNot ? 'line-through' : undefined
                            }
                          >
                            {getConditionParamLabel(condition.name, param, utilityNameMap)}
                          </Chip>
                        </span>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasInputs && (
          <div className={readOnly ? '' : 'pl-[34px]'}>
            <div className="mb-4 body-bold-300 text-text-primary">Inputs</div>
            {item.inputs!.map((input, inputIdx) => {
              const typeLabel = inputTypeOptions.find((o) => o.value === input.type)?.label || input.type;
              const hasInputConditions = input.conditions && input.conditions.length > 0;
              return (
                <div key={`${input.label}-${inputIdx}`} className="mb-4">
                  <div className="flex flex-wrap items-center gap-8">
                    <Chip variant="volt">{typeLabel}</Chip>
                    <PalmettoIcon name="chevronRight" size="xs" className="text-text-tertiary" />
                    <Chip variant="volt">{input.label}</Chip>
                    {input.required && <Chip variant="required">Required</Chip>}
                    {input.type === ChecklistItemInputType.requirement &&
                      input.options &&
                      input.options.length > 0 && (
                        <>
                          <PalmettoIcon name="chevronRight" size="xs" className="text-text-tertiary" />
                          {input.options.map((option, optIdx) => (
                            <span key={`${option.value}-${option.label}-${optIdx}`} className="contents">
                              <Chip variant="volt">{option.label}</Chip>
                              {option.decisionType && (
                                <Chip variant="lumen">
                                  {FlagPurposeLabel[option.decisionType as keyof typeof FlagPurposeLabel] ||
                                    option.decisionType}
                                </Chip>
                              )}
                            </span>
                          ))}
                        </>
                      )}
                    {input.type === ChecklistItemInputType.evaluation && input.evaluationKey && (
                      <>
                        <PalmettoIcon name="chevronRight" size="xs" className="text-text-tertiary" />
                        <Chip variant="volt">{evaluationKeyLabels[input.evaluationKey]}</Chip>
                        <EvaluationToleranceSummary
                          evaluationKey={input.evaluationKey}
                          params={input.evaluationParams}
                        />
                        {input.requirementFlagSettingId && flagSettingNameMap[input.requirementFlagSettingId] && (
                          <>
                            <PalmettoIcon name="chevronRight" size="xs" className="text-text-tertiary" />
                            <Chip variant="lumen">{flagSettingNameMap[input.requirementFlagSettingId]}</Chip>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {hasInputConditions && (
                    <div className="mt-2 flex flex-col gap-2 pl-16">
                      {input.conditions!.map((condition, idx) => {
                        const label =
                          ChecklistConditionNameLabels[condition.name as ChecklistConditionName] || condition.name;
                        return (
                          <div key={`${condition.name}-${idx}`} className="flex flex-wrap items-center gap-2">
                            <span
                              className={`font-medium body-300 ${idx === 0 ? 'invisible' : 'text-text-tertiary'}`}
                            >
                              and
                            </span>
                            <Chip variant="lumen">{label}</Chip>
                            <span className="body-300 text-text-tertiary">
                              {getOperatorDisplayText(condition.operator as ChecklistConditionOperator | undefined)}
                            </span>
                            {condition.params && condition.params.length > 0 && (
                              <>
                                {condition.params.map((param, paramIdx) => (
                                  <span key={param} className="contents">
                                    {paramIdx > 0 && <span className="mx-2 body-200 text-text-tertiary">or</span>}
                                    <Chip
                                      variant="lumen"
                                      className={
                                        condition.operator === ChecklistConditionOperator.IsNot
                                          ? 'line-through'
                                          : undefined
                                      }
                                    >
                                      {getConditionParamLabel(condition.name, param, utilityNameMap)}
                                    </Chip>
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {hasDocumentTypes && (
          <div className={readOnly ? '' : 'pl-[34px]'}>
            <div className="mb-4 body-bold-300 text-text-primary">Document Types</div>
            <div className="flex flex-wrap items-center gap-8">
              {item.documentTypes!.map((documentType) => (
                <Chip key={documentType} variant="success">
                  {documentTitles[documentType as keyof typeof documentTitles] || documentType}
                </Chip>
              ))}
            </div>
          </div>
        )}
        {item.description && (
          <div className={`flex items-center body-200 text-text-secondary ${readOnly ? '' : 'pl-[34px]'}`}>
            {item.description}
          </div>
        )}
      </div>
    </div>
  );
};
